import { Play } from "lucide-react";
import { Overlay } from "./ui/GameHud";
import { PauseActivePowerups } from "./PowerupHud";
import { PauseSkinRow } from "./PauseSkinRow";

/**
 * Pause overlay — score/lives/blocks card, active power-ups, endless-only
 * skin row + moonstone shop, and the Resume button.
 *
 * Extracted from `GameCanvas.jsx` on 2026-05-13. Renders nothing when
 * `paused` is false (the parent already gates on `paused && !gameOver
 * && !won`, but we double-check here so the component is safe to use
 * standalone in tests).
 *
 * Heavy on props because the moonstone shop is fully driven by parent
 * state (wallet balance, active shop-effect timestamps, flash id) and
 * fires back through the `onBuy` callback. Keeping that closure-free
 * was the goal of the extraction — GameCanvas no longer carries the
 * 130-line shop JSX inline.
 */
export function PauseOverlay({
  paused,
  gameOver,
  won,
  score,
  lives,
  blocks,
  activePowerups,
  powerupInfo,
  endless,
  walletCfg,
  previewSkin,
  onSwapSkin,
  onTryLockedSkin,
  moonstones,
  moonstoneShop,
  moonstoneFx,
  shopFlashId,
  gameNow,
  onBuy,
  onResume,
}) {
  if (!paused || gameOver || won) return null;
  return (
    <Overlay data-testid="pause-modal" onBackdropClick={onResume}>
      <div className="font-heading font-black uppercase text-4xl sm:text-5xl tracking-tighter text-[#00ffff] mb-2">
        Paused
      </div>
      <div
        className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-white/40 mb-5"
        data-testid="pause-tap-hint"
      >
        Tap outside to resume
      </div>
      <div
        className="grid grid-cols-3 gap-3 mb-5 w-full max-w-md"
        data-testid="pause-scorecard"
      >
        <PauseStat label="Score" value={score.toLocaleString()} color="#ffd700" />
        <PauseStat
          label="Lives"
          value={"♥".repeat(Math.max(0, lives))}
          color="#ff007f"
        />
        <PauseStat label="Blocks" value={blocks?.length ?? 0} color="#00ffff" />
      </div>
      <PauseActivePowerups powerups={activePowerups} info={powerupInfo} />
      <PauseSkinRow
        endless={endless}
        walletCfg={walletCfg}
        previewSkin={previewSkin}
        onSwap={onSwapSkin}
        onTry={onTryLockedSkin}
      />
      {endless && (
        <MoonstoneShop
          moonstones={moonstones}
          shop={moonstoneShop}
          fx={moonstoneFx}
          flashId={shopFlashId}
          gameNow={gameNow}
          onBuy={onBuy}
        />
      )}
      <button
        className="btn-neon cyan"
        onClick={onResume}
        data-testid="resume-btn"
      >
        <span className="inline-flex items-center gap-2">
          <Play className="w-4 h-4" /> Resume
        </span>
      </button>
    </Overlay>
  );
}

function PauseStat({ label, value, color }) {
  return (
    <div className="glass px-3 py-2" style={{ borderColor: `${color}66` }}>
      <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-0.5">
        {label}
      </div>
      <div
        className="font-heading font-black text-lg"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

function MoonstoneShop({ moonstones, shop, fx, flashId, gameNow, onBuy }) {
  // Shop was retired on 2026-05-30 — `shop` is now an empty object. Render
  // nothing so the pause overlay collapses cleanly without an empty header.
  if (!shop || Object.keys(shop).length === 0) return null;
  return (
    <div className="w-full max-w-md mb-5" data-testid="moonstone-shop">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#c084fc]">
          // Moonstone Shop
        </div>
        <div
          className="font-mono-cosmic text-[11px] tracking-widest uppercase text-[#c084fc] tabular-nums"
          data-testid="moonstone-balance"
        >
          {moonstones} <span className="text-white/50">stones</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(shop).map(([id, item]) => {
          const affordable = moonstones >= item.cost;
          const flashing = flashId === id;
          const now = gameNow();
          let activeLabel = null;
          if (id === "phase" && fx.phaseUntil > now)
            activeLabel = `${Math.max(0, ((fx.phaseUntil - now) / 1000)).toFixed(1)}s`;
          if (id === "dilation" && fx.dilationUntil > now)
            activeLabel = `${Math.max(0, ((fx.dilationUntil - now) / 1000)).toFixed(1)}s`;
          if (id === "plasma" && fx.plasmaUntil > now)
            activeLabel = `${Math.max(0, ((fx.plasmaUntil - now) / 1000)).toFixed(1)}s`;
          if (id === "shield" && fx.shieldArmed) activeLabel = "ARMED";
          return (
            <button
              key={id}
              type="button"
              onClick={() => onBuy(id)}
              disabled={!affordable}
              className={`text-left px-3 py-2 border bg-black/40 transition ${
                affordable
                  ? "hover:bg-white/[0.04] cursor-pointer"
                  : "opacity-45 cursor-not-allowed"
              } ${flashing ? "animate-pulse" : ""}`}
              style={{
                borderColor: `${item.color}aa`,
                boxShadow: flashing ? `0 0 18px ${item.color}99` : undefined,
              }}
              data-testid={`shop-buy-${id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span
                  className="font-heading uppercase text-xs sm:text-sm tracking-tight truncate"
                  style={{ color: item.color }}
                >
                  {item.label}
                </span>
                <span className="font-mono-cosmic text-[10px] tracking-widest uppercase text-[#c084fc] tabular-nums flex-shrink-0">
                  {item.cost}
                </span>
              </div>
              <div className="text-white/55 text-[10px] leading-snug">
                {item.desc}
              </div>
              {activeLabel && (
                <div
                  className="mt-1 font-mono-cosmic text-[9px] tracking-[0.25em] uppercase tabular-nums"
                  style={{ color: item.color }}
                  data-testid={`shop-active-${id}`}
                >
                  · ACTIVE {activeLabel}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
